'use client';

import React, { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Scribble from '@/app/components/suppliers/Scribble';

const PAINS = [
  'Nemám lidi na směny.', 'Marže jde dolů.', 'Dodavatel zase nedovezl.', 'Sezóna končí, co teď?', 'Energie stouply o 40 %.',
  'Kdo mi vezme sobotu?', 'Chci víc obědů ve všední den.', 'Kontrola z hygieny.', 'Zákazníci chodí jen v pátek.',
  'Kuchyň potřebuje novou pec.', 'Nemám čas řešit reklamu.', 'Chci stálé hosty, ne slevové lovce.',
];

const MAILS = ['Nabídka konvektomatů – 20 % sleva', 'Newsletter: novinky v našem sortimentu', 'Re: Re: Re: Ceník 2026', 'Katalog vybavení ke stažení'];

export default function ReachSection({ id }: { id: string }) {
  const root = useRef<HTMLElement>(null);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    // envelopes drop away, posts pop in – one orchestrated timeline
    const tl = gsap.timeline({ scrollTrigger: { trigger: '.sup-reach-stage', start: 'top 65%', once: true } });
    tl.from('.sup-envelope', { y: -30, opacity: 0, stagger: 0.12, duration: 0.5, ease: 'power3.out' })
      .to('.sup-envelope', { y: 220, rotate: (i) => (i % 2 ? 9 : -7), opacity: 0, stagger: 0.08, duration: 0.7, ease: 'power2.in' }, '+=0.6')
      .from('.sup-post', { y: 90, scale: 0.9, opacity: 0, stagger: 0.16, duration: 0.7, ease: 'back.out(1.6)' }, '-=0.35');
    gsap.from('.sup-reach-verdict', { opacity: 0, y: 16, duration: 0.6, scrollTrigger: { trigger: '.sup-reach-verdict', start: 'top 88%', once: true } });

    gsap.from('.sup-pain', {
      scale: 0.4, opacity: 0, rotate: () => gsap.utils.random(-14, 14), duration: 0.6, ease: 'back.out(2)',
      stagger: { each: 0.06, from: 'random' },
      scrollTrigger: { trigger: '.sup-pains', start: 'top 75%', once: true },
    });
  }, { scope: root });

  return (
    <section ref={ root } id={ id } className='container mt-20 md:mt-32'>
      <div className='sup-dark px-6 py-14 md:px-16 md:py-24 relative overflow-hidden'>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-center'>
          <div className='lg:col-span-6'>
            <h2 className='sup-h2'>Restauratéři<br /><span className='relative inline-block'>nečtou
              <Scribble kind='strike' color='var(--sup-heat)' width={ 7 } start='top 70%' style={ { left: '-4%', right: '-4%', top: '30%', height: '40%' } } />
            </span> e-maily.</h2>
            <p className='sup-body mt-8 opacity-90'>
              Víme to, protože jsme jim jich poslali tisíce. Avšak jako většina populace navštěvují sociální sítě – právě tady je zastihneme. Mezi směnou a objednávkou zboží, v jazyce, kterému rozumějí.
            </p>
            <p className='sup-reach-verdict sup-lead mt-8 text-primary' style={ { color: '#a8a3ff' } }>
              Nepíšeme jim do spamu. Jdeme jim naproti tam, kde opravdu jsou.
            </p>
          </div>

          <div className='sup-reach-stage lg:col-span-6 relative flex justify-center items-end'>
            {/* envelopes */}
            <div className='absolute left-0 right-0 top-0 flex flex-col gap-3 max-w-[340px] mx-auto z-10' aria-hidden='true'>
              { MAILS.map((m, i) => (
                <div key={ i } className='sup-envelope text-black' style={ { transform: `rotate(${ (i % 2 ? 1 : -1) * 1.5 }deg)` } }>
                  <span className='sup-envelope__dot' />
                  <span className='truncate'>{ m }</span>
                  <span className='ml-auto text-[0.7rem] opacity-50 font-medium'>nepřečteno</span>
                </div>
              )) }
            </div>
            {/* phone with feed */}
            <div className='sup-phone-tilt mt-12 md:mt-16' aria-hidden='true'>
            <div className='sup-phone'>
              <div className='sup-feed'>
                <div className='sup-post'>
                  <div className='flex items-center gap-2'><span className='w-6 h-6 rounded-full bg-primary inline-block' /><b>GastroStart</b><span className='opacity-50'>· Sponzorováno</span></div>
                  <div className='sup-post__img' />
                  <b>Nová pec do kuchyně bez zálohy?</b><br />Pro podniky, co vaří 100+ obědů denně.
                  <span className='sup-post__cta'>Chci vědět víc</span>
                </div>
                <div className='sup-post'>
                  <div className='flex items-center gap-2'><span className='w-6 h-6 rounded-full bg-black inline-block' /><b>Vaše značka</b><span className='opacity-50'>· Sponzorováno</span></div>
                  <div className='sup-post__img' style={ { background: 'linear-gradient(135deg,#111,#6c63ff)' } } />
                  <b>Káva, kterou hosté poznají po chuti.</b><br />Vzorek zdarma pro první kavárny v regionu.
                  <span className='sup-post__cta'>Objednat vzorek</span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

        <div className='mt-14 md:mt-20'>
          <h3 className='sup-h3 max-w-[24ch]'>
            Známe bolesti, obavy a přání <span className='relative inline-block whitespace-nowrap'>majitelů restaurací.
              <Scribble kind='circle' trigger='scrub' start='top 90%' end='top 55%' color='var(--sup-heat)' width={ 5 } style={ { left: '-3%', right: '-3%', top: '-8%', height: '116%' } } />
            </span>
          </h3>
          <p className='sup-body mt-6 opacity-85'>Používáme jazyk, kterému rozumějí. Tohle slýcháme každý týden:</p>
          <div className='sup-pains mt-10 flex flex-wrap gap-3 md:gap-4 max-w-[62rem]'>
            { PAINS.map((p, i) => (
              <span key={ i } className={ `sup-pain sup-chip ${ i % 5 === 3 ? 'sup-chip--heat' : '' }` } style={ { ['--tilt' as string]: `${ ((i * 7) % 5 - 2) * 1.4 }deg` } }>{ p }</span>
            )) }
          </div>
        </div>
      </div>
    </section>
  );
}
